import {
  provider as UI,
  Image,
  type ImageProps,
} from '@adobe-commerce/elsie/components';

import { getConfigValue } from '@adobe-commerce/elsie/lib/aem/configs';
import type { ResolveImageUrlOptions } from '../resolve-image';

const AEM_ASSETS_FORMATS = ['gif', 'jpg', 'jpeg', 'png', 'webp'] as const;
const AEM_ASSETS_ALLOWED_ROTATIONS = [90, 180, 270] as const;
const AEM_ASSETS_ALLOWED_FLIPS = ['h', 'v', 'hv'] as const;

/** The allowed formats for the `AEM Assets` image optimization API. */
export type AemAssetsFormat = (typeof AEM_ASSETS_FORMATS)[number];

/** The allowed rotations for the `AEM Assets` image optimization API. */
export type AemAssetsRotation = (typeof AEM_ASSETS_ALLOWED_ROTATIONS)[number];

/** The allowed flips for the `AEM Assets` image optimization API. */
export type AemAssetsFlip = (typeof AEM_ASSETS_ALLOWED_FLIPS)[number];

/**
 * Defines a crop region of an image.
 * @example
 * ```ts
 * // Crop the image to a 80% width and height, starting at 10% from the top and left.
 * const cropSettings: AemAssetsCropSettings = {
 *   xOrigin: 10,
 *   yOrigin: 10,
 *   width: 80,
 *   height: 80,
 * };
 */
export interface AemAssetsCropSettings {
  /** The (relative) x origin of the crop (between 0 and 100) */
  xOrigin?: number;

  /** The (relative) y origin of the crop (between 0 and 100) */
  yOrigin?: number;

  /** The width of the crop (between 0 and 100) */
  width?: number;

  /** The height of the crop (between 0 and 100) */
  height?: number;
}

/**
 * The parameters accepted by the `AEM Assets` image optimization API.
 * @see https://adobe-aem-assets-delivery-experimental.redoc.ly/
 */
export interface AemAssetsParams {
  format: AemAssetsFormat;
  rotate?: AemAssetsRotation;
  flip?: AemAssetsFlip;
  crop?: AemAssetsCropSettings;

  width?: number;
  height?: number;
  quality?: number;

  attachment?: boolean;
  sharpen?: boolean;
  blur?: number;
  dpr?: number;
  smartCrop?: string;

  // For future updates we may miss.
  [key: string]: unknown;
}

type WithRequired<T, K extends keyof T> = T & { [P in K]-?: T[P] };

/** The parameters to be applied to the asset (known width required when using a slot) */
export type AemAssetsImageSlotConfigParams = WithRequired<
  Partial<AemAssetsParams>,
  'width'
>;

/** The configuration for an image slot. */
export interface AemAssetsImageSlotConfig {
  /** The alias (i.e. seoName) of the image */
  alias: string;

  /** The props to be applied to the underlying {@link Image} component */
  imageProps: Partial<Omit<ImageProps, 'params' | 'width' | 'height'>> & {
    src: string;
  };

  /** The parameters to be applied to the asset (known width required when using a slot) */
  params: AemAssetsImageSlotConfigParams;

  /** The element that will contain the image in the slot */
  wrapper?: HTMLElement;
}

interface RenderContext {
  replaceWith: (element: HTMLElement) => void;
}

/**
 * Normalizes the given URL to ensure it is a valid URL.
 * @param {string} url - The URL to normalize.
 * @returns {string} The normalized URL.
 */
function normalizeUrl(url: string): string {
  let imageUrl = url;

  if (imageUrl.startsWith('//')) {
    // Use current window's protocol.
    const { protocol } = window.location;
    imageUrl = protocol + imageUrl;
  }

  return imageUrl;
}

/** Returns whether the given value is a valid flip. */
function isValidFlip(flip: unknown): flip is AemAssetsFlip {
  return AEM_ASSETS_ALLOWED_FLIPS.includes(flip as AemAssetsFlip);
}

/** Returns whether the given value is a valid rotation. */
function isValidRotation(rotation: unknown): rotation is AemAssetsRotation {
  return AEM_ASSETS_ALLOWED_ROTATIONS.includes(rotation as AemAssetsRotation);
}

/** Returns whether the given value is a valid format. */
function isValidFormat(format: unknown): format is AemAssetsFormat {
  return AEM_ASSETS_FORMATS.includes(format as AemAssetsFormat);
}

/** Asserts that the given value is valid. */
function assertUnionParameter(
  value: unknown,
  validator: (value: unknown) => boolean,
  errorMessage: string
): void {
  if (value !== undefined && !validator(value)) {
    throw new Error(errorMessage);
  }
}

/** Returns whether AEM Assets is enabled in the Storefront. */
export function isAemAssetsEnabled(): boolean {
  const config = getConfigValue('commerce-assets-enabled');

  return (
    config &&
    ((typeof config === 'string' && config.toLowerCase() === 'true') ||
      (typeof config === 'boolean' && config === true))
  );
}

/** The default optimization parameters used globally, unless overriden (per use). */
export function getDefaultAemAssetsOptimizationParams(): AemAssetsParams {
  // See: https://adobe-aem-assets-delivery-experimental.redoc.ly/
  return {
    quality: 80,
    format: 'webp',
  };
}

/** Returns true if the given URL is an AEM Assets URL. */
export function isAemAssetsUrl(url: string | URL): boolean {
  const assetsUrl = typeof url === 'string' ? new URL(normalizeUrl(url)) : url;

  if (!assetsUrl.pathname.startsWith('/adobe/assets/urn:aaid:aem')) {
    return false;
  }

  return true;
}

/** Generates an optimized URL for AEM Assets. */
export function generateAemAssetsOptimizedUrl(
  assetUrl: string,
  alias: string,
  params: Partial<AemAssetsParams> = {}
): string {
  const defaultParams = getDefaultAemAssetsOptimizationParams();
  const mergedParams: AemAssetsParams = { ...defaultParams, ...params };

  // Destructure the ones that need special handling/validation.
  const { format, crop, ...optimizedParams } = mergedParams;
  assertUnionParameter(format, isValidFormat, 'Invalid format');
  assertUnionParameter(optimizedParams.flip, isValidFlip, 'Invalid flip');
  assertUnionParameter(
    optimizedParams.rotate,
    isValidRotation,
    'Invalid rotation'
  );

  const stringifiedParams = Object.fromEntries(
    Object.entries(optimizedParams).map(([key, value]) => [key, String(value)])
  );

  const searchParams = new URLSearchParams(stringifiedParams);

  if (crop) {
    const [xOrigin, yOrigin] = [crop.xOrigin || 0, crop.yOrigin || 0];
    const [width, height] = [crop.width || 100, crop.height || 100];

    const cropTransform = `${xOrigin}p,${yOrigin}p,${width}p,${height}p`;
    searchParams.set('crop', cropTransform);
  }

  return `${assetUrl}/as/${alias}.${format}?${searchParams.toString()}`;
}

/**
 * Tries to generate an optimized URL for AEM Assets. Returns the given
 * url if AEM Assets is not enabled or is not an AEM Assets URL.
 */
export function tryGenerateAemAssetsOptimizedUrl(
  assetUrl: string,
  alias: string,
  params: Partial<AemAssetsParams> = {}
): string {
  const assetsEnabled = isAemAssetsEnabled();

  if (!assetsEnabled) {
    // No-op, doesn't do anything.
    return assetUrl;
  }

  const assetsUrl = new URL(normalizeUrl(assetUrl));

  if (!isAemAssetsUrl(assetsUrl)) {
    // Not an AEM Assets URL, so no-op.
    return assetUrl;
  }

  const base = assetsUrl.origin + assetsUrl.pathname;
  return generateAemAssetsOptimizedUrl(base, alias, params);
}

/** Creates a slot that renders an AEM Assets image. */
export function makeAemAssetsImageSlot(config: AemAssetsImageSlotConfig) {
  return (ctx: RenderContext) => {
    const { wrapper, alias, params, imageProps } = config;

    if (!imageProps.src) {
      throw new Error(
        'An image source is required. Please provide a `src` or `imageProps.src`.'
      );
    }

    const container = wrapper ?? document.createElement('div');
    const imageSrc = generateAemAssetsOptimizedUrl(
      imageProps.src,
      alias,
      params
    );

    const imageComponentParams: ResolveImageUrlOptions = {
      width: params.width,
      height: params.height,

      // If this is not done, they will be applied by default.
      // And they are not compatible with the AEM Assets API.
      crop: undefined,
      fit: undefined,
      auto: undefined,
    };

    const imageComponentProps: ImageProps = {
      ...imageProps,
      width: params.width,
      height: params.height,

      src: imageSrc,
      params: imageComponentParams,
    };

    UI.render(Image, imageComponentProps)(container);
    ctx.replaceWith(container);
  };
}

export function tryRenderAemAssetsImage(
  ctx: RenderContext,
  config: AemAssetsImageSlotConfig
): void {
  // Renders an equivalent of the default image.
  function renderDefaultImage(): void {
    const container = config.wrapper ?? document.createElement('div');
    const { imageProps, params } = config;
    const imageComponentProps: ImageProps = {
      ...imageProps,
      width: params.width,
      height: params.height,
    };

    UI.render(Image, imageComponentProps)(container);
    ctx.replaceWith(container);
  }

  const assetsEnabled = isAemAssetsEnabled();

  if (!assetsEnabled) {
    // No-op, render the default image.
    renderDefaultImage();
    return;
  }

  if (!config.imageProps.src) {
    throw new Error(
      'An image source is required. Please provide a `src` or `imageProps.src`.'
    );
  }

  const assetsUrl = new URL(normalizeUrl(config.imageProps.src));

  if (!isAemAssetsUrl(assetsUrl)) {
    // Not an AEM Assets URL, so render the default image.
    renderDefaultImage();
    return;
  }

  makeAemAssetsImageSlot(config)(ctx);
}
